import {
  CONSOLE_MESSAGE_LIMIT,
  CONSOLE_PROBLEM_LIMIT,
  type ConsoleSnapshot,
} from '@ai-studio/shared';
import { useConsole, type LogEntry } from '../state/console.js';
import { request } from './api.js';

export function consoleSnapshot(entries: readonly LogEntry[]): ConsoleSnapshot {
  const problems = entries.filter((e) => e.level === 'warn' || e.level === 'error');
  return {
    entries: problems.slice(-CONSOLE_PROBLEM_LIMIT).map((e) => ({
      level: e.level as 'warn' | 'error',
      source: e.source.slice(0, 120),
      message:
        e.message.length > CONSOLE_MESSAGE_LIMIT
          ? `${e.message.slice(0, CONSOLE_MESSAGE_LIMIT - 1)}…`
          : e.message,
      time: e.time,
      count: e.count,
    })),
    omitted: Math.max(0, problems.length - CONSOLE_PROBLEM_LIMIT),
  };
}

let active: { projectId: number; flush: () => Promise<void> } | null = null;

/** Finish syncing the current console before handing a new message to an agent. */
export async function flushConsole(projectId: number): Promise<void> {
  if (active?.projectId === projectId) await active.flush();
}

/** Sequential snapshots preserve clears/resolutions even on a slow connection. */
export function startConsoleSync(
  projectId: number,
  send?: (snapshot: ConsoleSnapshot) => Promise<unknown>,
): () => void {
  const controller = new AbortController();
  let stopped = false;
  let dirty = false;
  let pending: Promise<void> | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  const upload =
    send ??
    ((snapshot: ConsoleSnapshot) =>
      request(
        'PUT',
        `/projects/${projectId}/console`,
        snapshot,
        AbortSignal.any([controller.signal, AbortSignal.timeout(5000)]),
      ));
  const flush = (): Promise<void> => {
    dirty = true;
    if (pending) return pending;
    pending = (async () => {
      while (dirty && !stopped && useConsole.getState().projectId === projectId) {
        dirty = false;
        await upload(consoleSnapshot(useConsole.getState().entries));
      }
    })()
      .catch(() => {
        // Retry on the next update/heartbeat. Logging this failure would feed
        // the console back into itself; chat remains usable if sync is offline.
      })
      .finally(() => {
        pending = null;
      });
    return pending;
  };
  const handle = { projectId, flush };
  active = handle;
  const unsubscribe = useConsole.subscribe(() => {
    if (timer) return;
    timer = setTimeout(() => {
      timer = null;
      void flush();
    }, 200);
  });
  const heartbeat = setInterval(() => {
    void flush();
  }, 15_000);
  void flush();
  return () => {
    stopped = true;
    controller.abort();
    unsubscribe();
    if (timer) clearTimeout(timer);
    clearInterval(heartbeat);
    if (active === handle) active = null;
  };
}
